'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Type, Image, Video, LayoutGrid, Search } from 'lucide-react';

import { usePageBuilderContext } from '../page-builder-context';
import { BLOCK_TYPE_OPTIONS, type BlockType } from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';

// ============================================================================
// ICON MAP
// ============================================================================

const ICON_MAP: Record<string, typeof Type> = {
  Type,
  Image,
  Video,
  LayoutGrid,
};

function getBlockIcon(iconName: string) {
  return ICON_MAP[iconName] || Type;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function BlockInsertMenu() {
  const {
    isInsertMenuOpen,
    insertMenuAnchorId,
    closeInsertMenu,
    addBlock,
    setFocusedBlockId,
  } = usePageBuilderContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = BLOCK_TYPE_OPTIONS.filter((option) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      option.label.toLowerCase().includes(query) ||
      option.description.toLowerCase().includes(query) ||
      option.keywords.some((k) => k.toLowerCase().includes(query))
    );
  });

  // Reset on open
  useEffect(() => {
    if (isInsertMenuOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      // Focus input after a short delay for animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isInsertMenuOpen]);

  // Reset selected index when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isInsertMenuOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closeInsertMenu();
          break;

        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (filteredOptions[selectedIndex]) {
            handleSelectBlock(filteredOptions[selectedIndex].type);
          }
          break;
      }
    },
    [isInsertMenuOpen, filteredOptions, selectedIndex, closeInsertMenu]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close on click outside
  useEffect(() => {
    if (!isInsertMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeInsertMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isInsertMenuOpen, closeInsertMenu]);

  // Handle block selection
  const handleSelectBlock = (type: BlockType) => {
    const newBlockId = addBlock(type, insertMenuAnchorId ?? undefined);
    setFocusedBlockId(newBlockId);
    closeInsertMenu();
  };

  return (
    <AnimatePresence>
      {isInsertMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
          />

          {/* Menu */}
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className={cn(
              'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
              'w-full max-w-sm bg-card border border-border rounded-xl shadow-2xl overflow-hidden'
            )}
          >
            {/* Search header */}
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-tertiary" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск блоков..."
                  className={cn(
                    'w-full pl-9 pr-4 py-2 rounded-lg',
                    'bg-muted/50 border-none',
                    'text-sm placeholder:text-foreground-tertiary',
                    'focus:outline-none focus:ring-2 focus:ring-primary/20'
                  )}
                />
              </div>
            </div>

            {/* Options list */}
            <div className="p-2 max-h-72 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="py-8 text-center text-sm text-foreground-tertiary">
                  Ничего не найдено
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option, index) => {
                    const Icon = getBlockIcon(option.icon);
                    const isSelected = index === selectedIndex;

                    return (
                      <button
                        key={option.type}
                        type="button"
                        onClick={() => handleSelectBlock(option.type)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg text-left',
                          'transition-colors duration-100',
                          isSelected
                            ? 'bg-primary-subtle text-foreground'
                            : 'text-foreground-secondary hover:bg-muted/50'
                        )}
                      >
                        <div
                          className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                            isSelected
                              ? 'bg-primary-subtle-hover text-primary-text'
                              : 'bg-muted text-foreground-tertiary'
                          )}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{option.label}</div>
                          <div className="text-xs text-foreground-tertiary truncate">
                            {option.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-3 py-2 border-t border-border bg-muted/30">
              <div className="flex items-center justify-center gap-4 text-[10px] text-foreground-tertiary">
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-muted font-mono">↑↓</kbd> навигация
                </span>
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-muted font-mono">Enter</kbd> выбрать
                </span>
                <span>
                  <kbd className="px-1 py-0.5 rounded bg-muted font-mono">Esc</kbd> закрыть
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
